import React, { useState, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Tooltip,
    Stack,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';

import ReactTablePdfDownload from 'src/shared/components/table/components/ReactTablePdfDownload';
import OrderReportFilters from '../component/ReportFilter';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import { ReactTableXLSXDownload } from 'src/shared/components/table/components/ReactTableXLSXDownload';

const OrderOTRReport = () => {
    const [loading, setLoading] = useState(false);
    const [isReport, setReports] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [hub, setHub] = useState('');
    const [rider, setRider] = useState('');
    const [hubs, setHubs] = useState([]);
    const [riders, setRiders] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const excludedColumns = ['hubId', 'customerId', 'orderId', 'riderId','orderDate'];

    const columns = useMemo(
        () => (isReport.length > 0 ? Object.keys(isReport[0]) : []),
        [isReport]
    );

    const formatColumnName = (name) =>
        name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

    const replaceColons = (column, value) => {
        const columnsToReplace = ['riderName', 'hubName'];
        return columnsToReplace.includes(column) && typeof value === 'string'
            ? value.replace(/:/g, ' ')
            : value;
    };
    const columnLabelMap = {
        expectedMaxDeliveryTime: 'Exp. Max Delivery Time',

    };


    const filteredReport = useMemo(() => {
        return isReport.map(row =>
            Object.keys(row)
                .filter(col => !excludedColumns.includes(col))
                .reduce((obj, key) => {
                    obj[key] = row[key];
                    return obj;
                }, {})
        );
    }, [isReport]);


    const handleSearch = async () => {
        const payload = {
            selectedHubId: hub || null,
            selectedRiderId: rider || null,
            fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DDTHH:mm:ss') : null,
            toDate: toDate ? dayjs(toDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        };

        setLoading(true);
        setReports([]);

        try {
            const response = await PndRestApi.GenerateOTRReport(payload);
            if (response.status === 200 && response.data.status === 200) {
                setReports(response.data.data);
                console.log(response.data.data);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `Code: ${response.data.status}`,
                    response.data.message
                );
            }
        } catch (error) {
            console.error(error);
            showNotification(
                "theme-light",
                "ltr",
                "500",
                ERROR_MESSAGES.tryCatchError
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                ON THE ROAD REPORT
            </Typography>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
                <ReactTablePdfDownload
                    csvData={filteredReport}
                    fileName="OTR_Report_PDF"
                    title="ON THE ROAD REPORT"
                    portrait="portrait"
                    size="small"                // <-- add size small
                    variant="outlined"
                />
                <ReactTableXLSXDownload
                    csvData={filteredReport}
                    fileName="OTR_Report_Excel"
                    columnLabelMap={columnLabelMap}
                    size="small"                // <-- add size small
                    variant="outlined"
                />
            </Stack>

            <OrderReportFilters
                hub={hub}
                setHub={setHub}
                hubs={hubs}
                setHubs={setHubs}
                rider={rider}
                setRider={setRider}
                riders={riders}
                setRiders={setRiders}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                onSearch={handleSearch}
            />
            {filteredReport?.length > 0 ?
                <>
                    <TableContainer component={Paper} sx={{ maxHeight: 800 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ background: '#32ca73', color: '#fff' }}>S.No</TableCell>
                                    {columns.filter(col => !excludedColumns.includes(col)).map(col => (
                                        <TableCell key={col} sx={{ background: '#32ca73', color: '#fff' }}>
                                            {columnLabelMap[col] || formatColumnName(col)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredReport.length > 0 && (
                                    filteredReport
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                {Object.keys(row).map(col => (
                                                    <TableCell key={col}>
                                                        <Tooltip title={row[col] || ''}>
                                                            <span>{replaceColons(col, row[col])}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                )
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </> :
                (
                    <TableRow>
                        <TableCell colSpan={columns.length + 1} align="center" sx={{ color: 'error.main' }}>
                            No records found
                        </TableCell>
                    </TableRow>
                )
            }


            {filteredReport.length > 0 && (
                <TablePagination
                    component="div"
                    count={filteredReport.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Box>
    );
};

export default OrderOTRReport;
